/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : EnterSelectTextField.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, Autocomplete, TextField } from "@mui/material";
import InfoButton from "@mui/icons-material/Info";

function EnterSelectTextField({
  size = "small",
  label = "",
  placeholder = "",
  optionsList = [],
  required = false,
  value = "",
  setValue = () => {},
  txtFldStyle = {},
  valueError = "",
  disabled = false,
  type = "text",
  inputProps = {},
  readOnly = false,
  description = "",
  infoname = "",
}) {
  const muiStyle = {
    width: "100%",
    borderRadius: "0px",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "black",
        borderRadius: "0px",
      },
      "&:hover fieldset": {
        borderColor: "black",
      },
      "&.Mui-focused fieldset": {
        borderColor: "black",
      },
      "&.Mui-error fieldset": {
        borderColor: "#e53935",
      },
    },
    "& .MuiInputLabel-root": {
      color: "#000",
      fontSize: "0.9rem",
      fontWeight: 500,
      "&.Mui-focused": { color: "black" },
      "&.Mui-error": { color: "#e53935" },
      "&.Mui-disabled": { color: "#bdbdbd" },
    },
    "& .MuiInputBase-input::placeholder": {
      color: "#999",
      opacity: 1,
    },
    "& .MuiInputBase-input": {
      color: "black",
    },
    "& .MuiFormHelperText-root.Mui-error": {
      color: "#e53935",
      fontSize: "0.8rem",
      ml: 0,
    },
    ...txtFldStyle,
  };

  return (
    <Box>
      {label && (
        <Typography
          sx={{
            mb: 0.5,
            color: "#6f7174",
            fontSize: "0.9rem",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
          }}
        >
          {label}
          {required && <span style={{ color: "red", marginLeft: 4 }}>*</span>}
          {infoname && (
            <Box ml={1}>
              <InfoButton sx={{ fontSize: 14, color: "black" }} />
            </Box>
          )}
        </Typography>
      )}

      {description && (
        <Typography
          sx={{
            mb: 0.5,
            fontSize: "0.8rem",
            fontWeight: 500,
            color: "#6f7174",
          }}
        >
          {description}
        </Typography>
      )}

      <Autocomplete
        freeSolo
        disableClearable
        options={optionsList || []}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option?.label || ""
        }
        value={value}
        onInputChange={(e, newVal) => setValue(newVal)}
        onChange={(e, newVal) => setValue(newVal?.label ?? newVal ?? "")}
        disabled={disabled}
        readOnly={readOnly}
        sx={muiStyle}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            size={size}
            type={type}
            placeholder={placeholder}
            variant="outlined"
            error={Boolean(valueError)}
            helperText={valueError}
            inputProps={{ ...params.inputProps, ...inputProps }}
            InputProps={{
              ...params.InputProps,
              readOnly,
              sx: {
                backgroundColor: readOnly ? "#f5f5f5" : "#fff",
                color: "black",
              },
            }}
          />
        )}
      />
    </Box>
  );
}

EnterSelectTextField.propTypes = {
  size: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  optionsList: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          label: PropTypes.string,
          value: PropTypes.any,
        }),
      ]),
    ),
    PropTypes.shape({
      map: PropTypes.func,
    }),
  ]),
  required: PropTypes.bool,
  value: PropTypes.any,
  setValue: PropTypes.func,
  txtFldStyle: PropTypes.object,
  valueError: PropTypes.string,
  disabled: PropTypes.bool,
  type: PropTypes.string,
  inputProps: PropTypes.object,
  readOnly: PropTypes.bool,
  description: PropTypes.string,
  infoname: PropTypes.string,
};

export default EnterSelectTextField;
